import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, X, Repeat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, NativeSelect, StatusBadge, PriorityBadge, OverdueBadge, Empty, DemoBadge } from "@/components/common";
import { OrderFormDialog } from "@/components/OrderFormDialog";
import { STATUS, fmtWhen } from "@/lib/format";
import { cn } from "@/lib/utils";

const KPI_LABEL = { overdue: "Scaduti", no_motivation: "Scaduti senza motivazione", escalated: "In escalation", to_verify: "Da verificare", unassigned: "Da assegnare", suspended: "Sospesi", due_soon: "In scadenza 48h", open: "Aperti", closed: "Chiusi", late_history: "Con ritardo nello storico" };

export default function Orders() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [orders, setOrders] = useState(null);
  const [commesse, setCommesse] = useState([]);
  const [q, setQ] = useState(sp.get("q") || "");
  const [openNew, setOpenNew] = useState(false);
  const params = Object.fromEntries(sp.entries());

  const load = useCallback(() => {
    api.get("/orders", { params: Object.fromEntries(sp.entries()) }).then((r) => setOrders(r.data));
  }, [sp]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    api.get("/commesse").then((r) => setCommesse(r.data));
  }, []);

  const setParam = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    setSp(n);
  };
  const canCreate = ["ceo", "responsabile"].includes(user.role);
  const canManage = canCreate || (user.role === "caposquadra" && user.can_assign);
  const nav = useNavigate();

  return (
    <div data-testid="orders-page">
      <PageHeader title="Ordini di lavoro" subtitle={orders ? `${orders.length} ordini` : "…"}
        actions={<>
          {["ceo", "amministrazione", "responsabile"].includes(user.role) && <Button asChild variant="outline" className="h-11 rounded-xl font-bold"><Link to="/ricorrenze" data-testid="orders-recurrences-link"><Repeat className="mr-1 h-4 w-4" />Ricorrenti</Link></Button>}
          {canCreate && <Button onClick={() => setOpenNew(true)} className="h-11 rounded-xl font-bold" data-testid="orders-new-button"><Plus className="mr-1 h-4 w-4" />Nuovo ordine</Button>}
        </>} />

      <div className="mb-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        <form className="relative col-span-2 sm:w-72" onSubmit={(e) => { e.preventDefault(); setParam("q", q); }}>
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca codice, titolo, area…" className="h-10 rounded-xl bg-white pl-9" data-testid="orders-search-input" />
        </form>
        <NativeSelect testid="orders-filter-commessa" value={params.commessa_id} onChange={(v) => setParam("commessa_id", v)}>
          <option value="">Tutte le commesse</option>
          {commesse.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
        </NativeSelect>
        <NativeSelect testid="orders-filter-status" value={params.status} onChange={(v) => setParam("status", v)}>
          <option value="">Tutti gli stati</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </NativeSelect>
        <NativeSelect testid="orders-filter-priority" value={params.priority} onChange={(v) => setParam("priority", v)}>
          <option value="">Ogni priorità</option>
          {["alta", "normale"].map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
        </NativeSelect>
        <NativeSelect testid="orders-filter-kpi" value={params.kpi} onChange={(v) => setParam("kpi", v)}>
          <option value="">Tutti</option>
          {Object.entries(KPI_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </NativeSelect>
      </div>
      {Object.keys(params).length > 0 && (
        <button onClick={() => { setSp(new URLSearchParams()); setQ(""); }} className="mb-4 inline-flex items-center gap-1 rounded-full bg-accent px-3 py-1 text-xs font-semibold" data-testid="orders-clear-filters">
          <X className="h-3 w-3" /> Rimuovi filtri {params.kpi && `· ${KPI_LABEL[params.kpi]}`}
        </button>
      )}

      {orders && orders.length === 0 && <Empty text="Nessun ordine corrisponde ai filtri." testid="orders-empty" />}

      <div className="card-soft hidden overflow-hidden lg:block">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead className="bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="px-4 py-3">Codice</th><th className="px-4 py-3">Titolo</th><th className="px-4 py-3">Commessa / Cimitero</th><th className="px-4 py-3">Assegnatari</th><th className="px-4 py-3">Scadenza</th><th className="px-4 py-3">Priorità</th><th className="px-4 py-3">Stato</th></tr>
          </thead>
          <tbody>
            {orders?.map((o) => (
              <tr key={o.id} onClick={canManage ? () => nav(`/ordini/${o.id}`) : undefined}
                className={cn("border-t hover:bg-muted/40", canManage && "cursor-pointer", o.is_overdue && "overdue-row")} data-testid={`order-row-${o.code}`}>
                <td className="px-4 py-3 font-mono text-xs"><Link to={`/ordini/${o.id}`} className="font-semibold text-primary hover:underline" data-testid={`order-link-${o.code}`}>{o.code}</Link></td>
                <td className="px-4 py-3 font-semibold"><Link to={`/ordini/${o.id}`}>{o.title}</Link> <DemoBadge show={o.is_demo} /></td>
                <td className="px-4 py-3 text-muted-foreground">{o.commessa_code} · {o.cimitero_name || "—"}</td>
                <td className="px-4 py-3">{o.assignee_names.join(", ") || <span className="text-amber-700">Non assegnato</span>}</td>
                <td className={cn("px-4 py-3", o.is_overdue && "font-bold text-red-600")}>{fmtWhen(o)}<div><OverdueBadge order={o} testid={`order-overdue-${o.code}`} /></div></td>
                <td className="px-4 py-3"><PriorityBadge priority={o.priority} /></td>
                <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 lg:hidden">
        {orders?.map((o) => (
          <Link key={o.id} to={`/ordini/${o.id}`} data-testid={`order-card-${o.code}`} className={cn("card-soft block p-4", o.is_overdue && "overdue-row")}>
            <div className="flex items-center justify-between"><span className="font-mono text-xs text-muted-foreground">{o.code}</span><PriorityBadge priority={o.priority} /></div>
            <p className="mt-1 font-bold">{o.title}</p>
            <p className="mt-1 text-xs text-muted-foreground">{[o.commessa_code, o.cimitero_name, o.assignee_names.join(", ") || "non assegnato"].filter(Boolean).join(" · ")}</p>
            <p className="mt-1 text-xs font-semibold" data-testid={`order-card-when-${o.code}`}>{fmtWhen(o)}</p>
            <div className="mt-2 flex flex-wrap gap-2"><StatusBadge status={o.status} /><OverdueBadge order={o} /></div>
          </Link>
        ))}
      </div>
      {canCreate && <OrderFormDialog open={openNew} onOpenChange={setOpenNew} commesse={commesse} onCreated={load} />}
    </div>
  );
}
